import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { getProblemDetail } from '@/services/problemsApi';
import { getSubmissionById, getSubmissionResult } from '@/services/submissionsApi';
import { queryKeys } from '@/lib/queryKeys';
import { formatPassRatio, isSubmissionPending } from '@/lib/submissions';
import type { SubmissionResultSummary } from '@/services/types';
import PageLoading from '@/components/common/PageLoading';
import ContentApiErrorState, { ContentLoadError, ContentNotFound } from '@/components/common/ContentErrorState';
import SectionCard from '@/components/common/SectionCard';
import SubmitResultPanel from '@/components/submissions/SubmitResultPanel';
import SubmissionCodePanel from '@/components/submissions/SubmissionCodePanel';
import SubmissionCaseResults from '@/components/submissions/SubmissionCaseResults';
import SubmissionStatusBadge from '@/components/submissions/SubmissionStatusBadge';

export default function SubmissionDetailPage() {
  const { id } = useParams<{ id: string }>();

  const {
    data: submission,
    isLoading,
    isError,
    error: submissionError,
    refetch,
  } = useQuery({
    queryKey: queryKeys.submissions.detail(id ?? ''),
    queryFn: () => getSubmissionById(id ?? ''),
    enabled: Boolean(id),
    retry: 1,
  });

  const resultQuery = useQuery({
    queryKey: queryKeys.submissions.result(id ?? ''),
    queryFn: () => getSubmissionResult(id ?? ''),
    enabled: Boolean(id),
    retry: 1,
    refetchOnWindowFocus: false,
    refetchInterval: (query) => {
      const data = query.state.data as SubmissionResultSummary | undefined;
      const status = data?.status ?? submission?.status;
      return status && isSubmissionPending(status) ? 3000 : false;
    },
  });

  const problemQuery = useQuery({
    queryKey: queryKeys.problems.detail(submission?.problemId ?? ''),
    queryFn: () => getProblemDetail(submission?.problemId ?? ''),
    enabled: Boolean(submission?.problemId),
    retry: 1,
  });

  if (isLoading) {
    return <PageLoading message='提交详情加载中…' />;
  }

  if (!id) {
    return (
      <div className='max-w-[1080px] mx-auto px-6 py-12'>
        <ContentNotFound />
      </div>
    );
  }

  if (isError) {
    return (
      <div className='max-w-[1080px] mx-auto px-6 py-12'>
        <ContentApiErrorState error={submissionError} onRetry={() => void refetch()} />
      </div>
    );
  }

  if (!submission) {
    return (
      <div className='max-w-[1080px] mx-auto px-6 py-12'>
        <ContentLoadError onRetry={() => void refetch()} />
      </div>
    );
  }

  const status = resultQuery.data?.status ?? submission.status;

  return (
    <div className='max-w-[1080px] mx-auto px-6 py-12'>
      <div className='flex items-center justify-between gap-3 mb-6'>
        <Link to='/submissions' className='btn-ghost'>
          ‹ 返回提交记录
        </Link>
        <button
          type='button'
          className='btn btn-secondary'
          onClick={() => {
            void refetch();
            void resultQuery.refetch();
          }}
          disabled={resultQuery.isFetching}
        >
          {resultQuery.isFetching ? '刷新中…' : '刷新状态'}
        </button>
      </div>

      {resultQuery.isError ? (
        <div className='mb-5'>
          <ContentApiErrorState error={resultQuery.error} onRetry={() => void resultQuery.refetch()} />
        </div>
      ) : null}

      <SectionCard title='提交信息' className='mb-5'>
        <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
          <InfoRow label='提交 ID' value={submission.id} />
          <div>
            <p className='text-apple-gray text-[0.8125rem] mb-1'>当前状态</p>
            <SubmissionStatusBadge status={status} />
          </div>
          <InfoRow label='提交时间' value={formatDateTime(submission.createdAt)} />
          <InfoRow label='最近更新' value={formatDateTime(submission.updatedAt)} />
          <div>
            <p className='text-apple-gray text-[0.8125rem] mb-1'>题目</p>
            <Link to={`/problems/${submission.problemId}`} className='text-apple-blue-link hover:underline text-[0.9375rem]'>
              {problemQuery.data?.title ?? submission.problemId}
            </Link>
          </div>
          <InfoRow
            label='通过率'
            value={formatPassRatio(resultQuery.data?.passRatio ?? submission.result?.passRatio ?? 0)}
          />
        </div>
      </SectionCard>

      <div className='mb-5'>
        <SubmitResultPanel status={status} result={resultQuery.data ?? submission.result} />
      </div>

      <div className='mb-5'>
        <SubmissionCaseResults result={resultQuery.data ?? submission.result} />
      </div>

      <SubmissionCodePanel code={submission.code} />
    </div>
  );
}

interface InfoRowProps {
  label: string;
  value: string;
}

function InfoRow({ label, value }: InfoRowProps) {
  return (
    <div>
      <p className='text-apple-gray text-[0.8125rem] mb-1'>{label}</p>
      <p className='text-apple-near-black text-[0.9375rem] break-all'>{value}</p>
    </div>
  );
}

function formatDateTime(value: string) {
  return new Date(value).toLocaleString('zh-CN', {
    hour12: false,
  });
}
